// src/widgets/time-widget/timeWidget.utils.ts

import type { PeriodSelection, ServiceType } from './timeWidget.types'
import type { TimeframeKey } from '@/ui/components/utils/worktimeTransforms'

// ---------- date helpers ----------
const pad2 = (n: number) => String(n).padStart(2, '0')

export function toISODate(d: Date) {
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`
}

export function parseISODate(iso: string) {
  // iso: YYYY-MM-DD
  const [y, m, d] = iso.split('-').map(Number)
  return new Date(y, m - 1, d)
}

export function startOfDay(d: Date) {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate(), 0, 0, 0, 0)
}
export function endOfDay(d: Date) {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate(), 23, 59, 59, 999)
}

export function startOfMonth(year: number, month1to12: number) {
  return new Date(year, month1to12 - 1, 1, 0, 0, 0, 0)
}
export function endOfMonth(year: number, month1to12: number) {
  return new Date(year, month1to12, 0, 23, 59, 59, 999)
}

export function addDays(d: Date, days: number) {
  const x = new Date(d)
  x.setDate(x.getDate() + days)
  return x
}

// ---------- period -> range + granularity ----------
export function getPeriodRange(
  selection: PeriodSelection,
  timeframe?: TimeframeKey
): {
  from: Date
  to: Date
  label: string
  granularity: 'day' | 'week' | 'month' | 'quarter' | 'year'
} {
  const daysBetween = (a: Date, b: Date) => {
    const ms = b.getTime() - a.getTime()
    return Math.max(0, Math.ceil(ms / (1000 * 60 * 60 * 24)))
  }

  const tf: TimeframeKey = timeframe ?? 'Monat'
  const granFromTf = (t: TimeframeKey): 'day' | 'week' | 'month' | 'quarter' | 'year' => {
    if (t === 'Woche') return 'week'
    if (t === 'Monat') return 'month'
    if (t === 'Jahr') return 'month'
    if (t === 'Quartal') return 'quarter'
    return 'month'
  }
  switch (selection.kind) {
    case 'YTD': {
      const now = new Date()
      const from = new Date(now.getFullYear(), 0, 1)
      const to = endOfDay(now)
      return { from, to, label: `YTD ${now.getFullYear()}`, granularity: 'month' }
    }
    case 'YEAR': {
      const from = new Date(selection.year, 0, 1)
      const to = new Date(selection.year, 11, 31, 23, 59, 59, 999)
      return { from, to, label: `${selection.year}`, granularity: granFromTf(tf) }
    }
    case 'QUARTER': {
      const from = new Date(selection.year, 0, 1)
      const to = new Date(selection.year, 11, 31, 23, 59, 59, 999)
      return {
        from,
        to,
        label: `${selection.year}`,
        granularity: 'quarter',
      }
    }
    case 'MONTH': {
      const from = startOfMonth(selection.year, selection.month)
      const to = endOfMonth(selection.year, selection.month)
      return {
        from,
        to,
        label: `${pad2(selection.month)}.${selection.year}`,
        granularity: granFromTf(tf),
      }
    }
    case 'WEEK': {
      const from = startOfDay(parseISODate(selection.isoWeekStart))
      const to = endOfDay(addDays(from, 6))
      return { from, to, label: `Woche ab ${selection.isoWeekStart}`, granularity: granFromTf(tf) }
    }
    case 'CUSTOM': {
      const from = startOfDay(parseISODate(selection.from))
      const to = endOfDay(parseISODate(selection.to))
      const days = Math.ceil((to.getTime() - from.getTime()) / (24 * 3600 * 1000)) + 1

      if (tf === 'Quartal') {
        return { from, to, label: `${selection.from} – ${selection.to}`, granularity: 'month' }
      }
      if (tf === 'Jahr') {
        return { from, to, label: `${selection.from} – ${selection.to}`, granularity: 'month' }
      }
      const granularity: 'day' | 'week' | 'month' | 'quarter' | 'year' =
        days <= 35 ? 'day' : days <= 120 ? 'week' : 'month'

      return { from, to, label: `${selection.from} – ${selection.to}`, granularity }
    }
  }
}

// ---------- bucketing ----------
export function bucketKey(d: Date, granularity: 'day' | 'week' | 'month' | 'quarter' | 'year') {
  const y = d.getFullYear()
  const m = d.getMonth() + 1
  const day = d.getDate()

  if (granularity === 'year') return `${y}`

  if (granularity === 'quarter') {
    const q = Math.floor(d.getMonth() / 3) + 1 // 1..4
    return `${d.getFullYear()}-Q${q}`
  }
  if (granularity === 'day') return `${y}-${pad2(m)}-${pad2(day)}`
  if (granularity === 'month') return `${y}-${pad2(m)}`

  // week: bucket by Monday start (simple ISO-like week start)
  const tmp = startOfDay(d)
  const weekday = (tmp.getDay() + 6) % 7 // Mon=0 ... Sun=6
  const monday = addDays(tmp, -weekday)
  return toISODate(monday)
}

function week1Monday(year: number) {
  // Monday of the week that contains Jan 1st of "year"
  const jan1 = new Date(year, 0, 1)
  const tmp = startOfDay(jan1)
  const weekday = (tmp.getDay() + 6) % 7 // Mon=0 ... Sun=6
  return addDays(tmp, -weekday)
}

export function weekNumberFromYearStart(monday: Date, contextYear: number) {
  const w1 = week1Monday(contextYear)
  const diffDays = Math.floor((startOfDay(monday).getTime() - w1.getTime()) / (24 * 3600 * 1000))
  return Math.floor(diffDays / 7) + 1
}

export function bucketLabel(
  key: string,
  granularity: 'day' | 'week' | 'month' | 'quarter' | 'year',
  contextYear?: number
) {
  if (granularity === 'year') return key

  if (granularity === 'quarter') {
    const [, q] = key.split('-')
    const y = key.split('-')[0]
    return `${q} ${y}`
  }
  if (granularity === 'month') return key // YYYY-MM
  if (granularity === 'week') {
    const y = contextYear ?? new Date().getFullYear()
    const monday = parseISODate(key)
    const kw = weekNumberFromYearStart(monday, y)
    return `KW-${kw}`
  }
  return key // day YYYY-MM-DD
}

export function makeServiceType(activityType: string): ServiceType | undefined {
  const t = String(activityType).toUpperCase()
  if (t === 'TOJ_CUSTOMER') return 'TOJ'
  if (t === 'TOJ') return 'TOJ'
  if (t === 'CONSULTING') return 'CONSULTING'
  if (t === 'TRAINING') return 'TRAINING'
  if (t === 'PILOT') return 'PILOT'
  return undefined
}

// ---------- distribute ordered hours across buckets ----------
export function clampRange(range: { from: Date; to: Date }, toClamp: { from: Date; to: Date }) {
  const from = new Date(Math.max(range.from.getTime(), toClamp.from.getTime()))
  const to = new Date(Math.min(range.to.getTime(), toClamp.to.getTime()))
  if (from.getTime() > to.getTime()) return null
  return { from, to }
}

export function enumerateBucketStarts(
  from: Date,
  to: Date,
  granularity: 'day' | 'week' | 'month' | 'quarter' | 'year'
) {
  const out: Date[] = []
  let cur = startOfDay(from)

  if (granularity === 'year') {
    cur = new Date(cur.getFullYear(), 0, 1)
  }

  if (granularity === 'quarter') {
    const qm = Math.floor(cur.getMonth() / 3) * 3
    cur = new Date(cur.getFullYear(), qm, 1)
  }
  if (granularity === 'month') cur = new Date(cur.getFullYear(), cur.getMonth(), 1)
  if (granularity === 'week') {
    const weekday = (cur.getDay() + 6) % 7
    cur = addDays(cur, -weekday)
  }

  while (cur.getTime() <= to.getTime()) {
    out.push(new Date(cur))
    if (granularity === 'day') cur = addDays(cur, 1)
    else if (granularity === 'week') cur = addDays(cur, 7)
    else if (granularity === 'month') cur = new Date(cur.getFullYear(), cur.getMonth() + 1, 1)
    else if (granularity === 'quarter') cur = new Date(cur.getFullYear(), cur.getMonth() + 3, 1)
    else cur = new Date(cur.getFullYear() + 1, 0, 1)
  }
  return out
}

export function getFetchRange(selection: PeriodSelection): { from: Date; to: Date } {
  switch (selection.kind) {
    case 'YTD': {
      const now = new Date()
      return {
        from: new Date(now.getFullYear(), 0, 1),
        to: endOfDay(now),
      }
    }
    case 'YEAR':
      return {
        from: new Date(selection.year, 0, 1),
        to: new Date(selection.year, 11, 31, 23, 59, 59, 999),
      }

    case 'QUARTER':
      return {
        from: new Date(selection.year, 0, 1),
        to: new Date(selection.year, 11, 31, 23, 59, 59, 999),
      }

    case 'MONTH':
      return {
        from: startOfMonth(selection.year, selection.month),
        to: endOfMonth(selection.year, selection.month),
      }

    case 'WEEK': {
      const from = startOfDay(parseISODate(selection.isoWeekStart))
      const to = endOfDay(addDays(from, 6))
      return { from, to }
    }

    case 'CUSTOM':
      return {
        from: startOfDay(parseISODate(selection.from)),
        to: endOfDay(parseISODate(selection.to)),
      }
  }
}

export function distributeHoursEvenly(args: {
  totalHours: number
  from: Date
  to: Date
  periodFrom: Date
  periodTo: Date
  granularity: 'day' | 'week' | 'month' | 'quarter' | 'year'
}): Map<string, number> {
  const { totalHours, from, to, periodFrom, periodTo, granularity } = args

  const clamped = clampRange({ from, to }, { from: periodFrom, to: periodTo })
  if (!clamped) return new Map()

  const starts = enumerateBucketStarts(clamped.from, clamped.to, granularity)
  if (starts.length === 0) return new Map()

  const per = totalHours / starts.length
  const m = new Map<string, number>()
  for (const s of starts) {
    const key = bucketKey(s, granularity)
    m.set(key, (m.get(key) ?? 0) + per)
  }
  return m
}
